import { useEffect, useRef } from "react";
import type { FilterState, SearchConfig, SortState } from "../types/DataControls";
import { useDebounce } from "./useDebounce";

export interface ServerDataState {
  filters: FilterState;
  sort: SortState | null;
  search: SearchConfig | null;
}

export interface UseServerDataControlsOptions {
  /** Filter state */
  filters: FilterState;
  /** Sort state */
  sort: SortState | null;
  /** Search configuration */
  search: SearchConfig | null;
  /** Callback when state changes (debounced for search) */
  onStateChange: (state: ServerDataState) => void;
  /** Search debounce delay in ms (default: 300) */
  searchDebounce?: number;
}

/**
 * useServerDataControls
 *
 * Server-side hook that notifies the parent when filter/sort/search state changes.
 * Search queries are debounced to avoid excessive API calls.
 *
 * This hook doesn't manipulate data - it just provides a clean way to:
 * 1. Debounce search input
 * 2. Notify when any state changes
 * 3. Trigger server refetch
 *
 * @example
 * ```tsx
 * const [filters, setFilters] = useState(null);
 * const [sort, setSort] = useState({ field: 'name', direction: 'asc' });
 * const [search, setSearch] = useState({ query: '', fields: ['name'] });
 *
 * useServerDataControls({
 *   filters,
 *   sort,
 *   search,
 *   onStateChange: (state) => {
 *     // Refetch data with new parameters
 *     refetch({
 *       filters: state.filters,
 *       sort: state.sort,
 *       search: state.search?.query
 *     });
 *   }
 * });
 * ```
 */
export function useServerDataControls({
  filters,
  sort,
  search,
  onStateChange,
  searchDebounce = 300,
}: UseServerDataControlsOptions): void {
  // Debounce search query to avoid excessive API calls
  const debouncedSearch = useDebounce(search, searchDebounce);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Notify parent when state changes
  useEffect(() => {
    // Skip the initial mount to avoid double-firing on component mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    onStateChange({
      filters,
      sort,
      search: debouncedSearch,
    });
  }, [filters, sort, debouncedSearch, onStateChange]);
}
